// Existing state vs work performed in the visit (docs/API.md "Entry rules").
import type { EntryKind } from '@/types/api';
import type { Code } from './dental';

/** Findings describe what is there: always EXISTING (pathology shows red). */
export const FINDING_CODES: ReadonlySet<Code> = new Set<Code>([
  'caries',
  'fracture',
  'periapical',
  'root_remnant',
  'devital',
  'missing',
  'mobility',
  'calculus',
  'impacted',
  'wear',
  'sound',
]);

/** Findings that need treatment: shown red on the chart. */
export const PATHOLOGY_CODES: ReadonlySet<Code> = new Set<Code>([
  'caries',
  'fracture',
  'periapical',
  'root_remnant',
  'mobility',
  'calculus',
  'wear',
]);

export function entryKindFor(code: Code, requested: EntryKind | undefined): EntryKind {
  if (FINDING_CODES.has(code)) return 'EXISTING';
  return requested ?? 'PERFORMED';
}

/**
 * Words that say a restoration was already there ("stara plomba", "postojeća
 * kruna", "régi tömés", "old filling", "gammel fylling"), in every UI language.
 */
const EXISTING_WORDS =
  /(?<!\p{L})(star[aeiou]m?|postoje[cćč]\p{L}*|zate[cč]en\p{L}*|r[ée]gi|megl[ée]v[őo]|kor[aá]bbi|old|existing|previous|gammel\p{L}*|eksisterende|tidligere)(?!\p{L})/iu;

/**
 * Separators between the clauses of one sentence ("26 plomba, 46 stara kruna").
 * Not "a": it is an article in Hungarian and English ("régi tömés a 26-oson").
 */
const CLAUSE_BREAK = /[,;.]|\s(?:i|és|and|og)\s/iu;

/**
 * The part of a sentence about `tooth`: one dictated sentence can name several
 * teeth, and "stara" before 46 must not make 26 existing too.
 */
function clauseAbout(text: string, tooth: number | undefined): string {
  if (tooth === undefined) return text;
  const own = text
    .split(CLAUSE_BREAK)
    .find((part) => new RegExp(`(?<!\\d)${tooth}(?!\\d)`).test(part));
  if (own) return own;
  // Other teeth are named by number but this one is not (said in words):
  // their "old" must not spill over, so nothing is stated for it.
  return /\d/.test(text) ? '' : text;
}

/** The kind the clause about `tooth` states in words, if any. */
export function statedKind(text: string | null | undefined, tooth?: number): EntryKind | undefined {
  return text && EXISTING_WORDS.test(clauseAbout(text, tooth)) ? 'EXISTING' : undefined;
}
